export const HOGIA_ID_BROWSER_TRANSACTION_STORAGE_KEY = '_dhHogiaIdTransaction.v1';
export const HOGIA_ID_BROWSER_TRANSACTION_MAX_AGE_MS = 15 * 60 * 1_000;
export const HOGIA_ID_32_BYTE_BASE64URL_VALUE_LENGTH = 43;

const LEGACY_HOGIA_ID_LOGIN_TRANSACTION_STORAGE_KEY = '_dhHogiaIdLogin.v1';
const RANDOM_VALUE_BYTE_LENGTH = 32;
const BASE64URL_PATTERN = /^[A-Za-z0-9_-]+$/;

interface SessionStorageArea {
  getItem(key: string): string | null;
  removeItem(key: string): void;
  setItem(key: string, value: string): void;
}

interface StoredHogiaIdBrowserTransaction {
  version: 1;
  state: string;
  codeVerifier: string;
  createdAtEpochMs: number;
}

export interface HogiaIdBrowserTransactionStart {
  state: string;
  codeChallenge: string;
}

export interface HogiaIdBrowserTransactionRuntime {
  getSessionStorage(): SessionStorageArea;
  getRandomValues(destination: Uint8Array<ArrayBuffer>): void;
  sha256(value: BufferSource): Promise<ArrayBuffer>;
  now(): number;
}

const browserRuntime: HogiaIdBrowserTransactionRuntime = {
  getSessionStorage: () => window.sessionStorage,
  getRandomValues: destination => {
    window.crypto.getRandomValues(destination);
  },
  sha256: value => window.crypto.subtle.digest('SHA-256', value),
  now: () => Date.now(),
};

export class HogiaIdBrowserTransactionStore {
  constructor(private readonly runtime: HogiaIdBrowserTransactionRuntime = browserRuntime) {}

  async create(): Promise<HogiaIdBrowserTransactionStart | undefined> {
    let storage: SessionStorageArea;

    try {
      storage = this.runtime.getSessionStorage();
    } catch {
      return undefined;
    }

    if (!clearStoredTransaction(storage)) {
      return undefined;
    }

    try {
      const state = this.generateRandomValue();
      const codeVerifier = this.generateRandomValue();
      const codeChallenge = await deriveS256CodeChallengeWithRuntime(codeVerifier, this.runtime);
      const createdAtEpochMs = this.runtime.now();

      if (!codeChallenge || !Number.isSafeInteger(createdAtEpochMs) || createdAtEpochMs < 0) {
        clearStoredTransaction(storage);
        return undefined;
      }

      const storedTransaction: StoredHogiaIdBrowserTransaction = {
        version: 1,
        state,
        codeVerifier,
        createdAtEpochMs,
      };
      const serializedTransaction = JSON.stringify(storedTransaction);

      storage.setItem(HOGIA_ID_BROWSER_TRANSACTION_STORAGE_KEY, serializedTransaction);
      if (storage.getItem(HOGIA_ID_BROWSER_TRANSACTION_STORAGE_KEY) !== serializedTransaction) {
        clearStoredTransaction(storage);
        return undefined;
      }

      return { state, codeChallenge };
    } catch {
      clearStoredTransaction(storage);
      return undefined;
    }
  }

  consume(callbackState: string): string | undefined {
    let storage: SessionStorageArea;

    try {
      storage = this.runtime.getSessionStorage();
    } catch {
      return undefined;
    }

    let serializedTransaction: string | null = null;
    try {
      serializedTransaction = storage.getItem(HOGIA_ID_BROWSER_TRANSACTION_STORAGE_KEY);
      storage.removeItem(HOGIA_ID_BROWSER_TRANSACTION_STORAGE_KEY);

      // The verifier is one-shot. If removal cannot be verified, do not release it
      // to the caller for redemption.
      if (storage.getItem(HOGIA_ID_BROWSER_TRANSACTION_STORAGE_KEY) !== null) {
        clearStoredTransaction(storage);
        return undefined;
      }
    } catch {
      clearStoredTransaction(storage);
      return undefined;
    }

    const transaction = parseStoredTransaction(serializedTransaction);
    const now = this.runtime.now();
    if (
      !transaction ||
      !isHogiaId32ByteBase64UrlValue(callbackState) ||
      transaction.state !== callbackState ||
      !Number.isSafeInteger(now) ||
      now < transaction.createdAtEpochMs ||
      now - transaction.createdAtEpochMs > HOGIA_ID_BROWSER_TRANSACTION_MAX_AGE_MS
    ) {
      return undefined;
    }

    return transaction.codeVerifier;
  }

  clear(): boolean {
    try {
      return clearStoredTransaction(this.runtime.getSessionStorage());
    } catch {
      return false;
    }
  }

  private generateRandomValue(): string {
    const bytes = new Uint8Array(RANDOM_VALUE_BYTE_LENGTH);
    this.runtime.getRandomValues(bytes);
    const encoded = encodeBase64Url(bytes);

    if (!isHogiaId32ByteBase64UrlValue(encoded)) {
      throw new Error('Web Crypto returned an invalid random value.');
    }

    return encoded;
  }
}

export async function deriveS256CodeChallenge(codeVerifier: string): Promise<string | undefined> {
  return deriveS256CodeChallengeWithRuntime(codeVerifier, browserRuntime);
}

/**
 * Builds a canonical HogiaID start URL by enforcing the configured root and appending `start=1`.
 *
 * The `start=1` query parameter tells DhHogiaId that it should discard any pending transaction
 * and start a new one.
 *
 * @param configuredRootUrl The root URL from configuration.
 * @returns The canonical HogiaID start URL with `start=1`, or `undefined` if config is invalid (fails closed).
 */
export function buildDhHogiaIdStartUrl(configuredRootUrl: string): string | undefined {
  const url = parseDhHogiaIdRootUrl(configuredRootUrl);
  if (!url) {
    return undefined;
  }

  url.searchParams.set('start', '1');
  return url.toString();
}

export function buildDhHogiaIdBootstrapReturnUrl(
  configuredRootUrl: string,
  hState: string,
  transaction: HogiaIdBrowserTransactionStart
): string | undefined {
  const url = parseDhHogiaIdRootUrl(configuredRootUrl);
  if (
    !url ||
    !isHogiaId32ByteBase64UrlValue(hState) ||
    !isHogiaId32ByteBase64UrlValue(transaction?.state) ||
    !isHogiaId32ByteBase64UrlValue(transaction?.codeChallenge)
  ) {
    return undefined;
  }

  url.searchParams.set('hstate', hState);
  url.searchParams.set('state', transaction.state);
  url.searchParams.set('code_challenge', transaction.codeChallenge);
  return url.toString();
}

/**
 * Validates the canonical DhHogiaId frontend root used for both normalization starts and
 * bootstrap returns. Invalid or non-canonical configuration fails closed.
 */
function parseDhHogiaIdRootUrl(configuredRootUrl: string): URL | undefined {
  // URL.search and URL.hash collapse bare trailing delimiters to empty strings.
  if (
    !configuredRootUrl ||
    configuredRootUrl.trim() !== configuredRootUrl ||
    configuredRootUrl.includes('?') ||
    configuredRootUrl.includes('#')
  ) {
    return undefined;
  }

  try {
    const url = new URL(configuredRootUrl);
    if (url.protocol !== 'https:' || !url.hostname || url.username || url.password || url.pathname !== '/' || url.search || url.hash) {
      return undefined;
    }

    return url;
  } catch {
    return undefined;
  }
}

async function deriveS256CodeChallengeWithRuntime(
  codeVerifier: string,
  runtime: Pick<HogiaIdBrowserTransactionRuntime, 'sha256'>
): Promise<string | undefined> {
  if (!isHogiaId32ByteBase64UrlValue(codeVerifier)) {
    return undefined;
  }

  try {
    const digest = await runtime.sha256(new TextEncoder().encode(codeVerifier));
    const challenge = encodeBase64Url(new Uint8Array(digest));
    return isHogiaId32ByteBase64UrlValue(challenge) ? challenge : undefined;
  } catch {
    return undefined;
  }
}

function parseStoredTransaction(serializedTransaction: string | null): StoredHogiaIdBrowserTransaction | undefined {
  if (!serializedTransaction) {
    return undefined;
  }

  try {
    const value = JSON.parse(serializedTransaction) as unknown;
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return undefined;
    }

    const record = value as Record<string, unknown>;
    if (
      Object.keys(record).length !== 4 ||
      record.version !== 1 ||
      !isHogiaId32ByteBase64UrlValue(record.state) ||
      !isHogiaId32ByteBase64UrlValue(record.codeVerifier) ||
      !Number.isSafeInteger(record.createdAtEpochMs) ||
      (record.createdAtEpochMs as number) < 0
    ) {
      return undefined;
    }

    return record as unknown as StoredHogiaIdBrowserTransaction;
  } catch {
    return undefined;
  }
}

function clearStoredTransaction(storage: SessionStorageArea): boolean {
  try {
    storage.removeItem(HOGIA_ID_BROWSER_TRANSACTION_STORAGE_KEY);
    storage.removeItem(LEGACY_HOGIA_ID_LOGIN_TRANSACTION_STORAGE_KEY);
    return (
      storage.getItem(HOGIA_ID_BROWSER_TRANSACTION_STORAGE_KEY) === null &&
      storage.getItem(LEGACY_HOGIA_ID_LOGIN_TRANSACTION_STORAGE_KEY) === null
    );
  } catch {
    return false;
  }
}

/**
 * Validates the exact 43-character, unpadded base64url shape used for this
 * flow's state, verifier, and challenge. This is not a general base64url validator.
 */
export function isHogiaId32ByteBase64UrlValue(value: unknown): value is string {
  return typeof value === 'string' && value.length === HOGIA_ID_32_BYTE_BASE64URL_VALUE_LENGTH && BASE64URL_PATTERN.test(value);
}

function encodeBase64Url(value: Uint8Array<ArrayBuffer>): string {
  let binary = '';
  for (const byte of value) {
    binary += String.fromCharCode(byte);
  }

  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}
